import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'
import { forkJoin, Observable, Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'

import { AuthService } from '@app/modules/auth/auth.service'
import { ToolbarService } from './toolbar.service'
import { NotificationService } from './notifications/notification.service'
import { NotificationMessage } from './notifications/notification-message.model'
import { Setting } from '@shared/models/setting.model'
import { SettingsService } from '@shared/common-components/settings/settings.service'
import { PlatformSettingsService } from '@core/services/platfrom-settings.service'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { PermissionsService } from '@shared/services/permissions.service'
import { PermissionSourceType, PermissionsSource } from '@core/types/permissions.source'
import { FeatureFlagService } from '@core/services/feature-flag.service'
import { SosService } from './sos/sos.service'
import { safeHttpUrl } from '@core/helpers/safe-url'

@Component({
  selector: 'lin-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  standalone: false,
})
export class ToolbarComponent implements OnInit, OnDestroy {
  user = this.authService.getCurrentUser()
  unreadNotificationsCount = 0
  isNotificationPopup: boolean
  isNotificationSound: boolean
  isNotificationsPermission: boolean
  isMapPage = false

  @ViewChild('notificationList', { static: false }) notificationList: any

  private supportUrl: string
  private readonly onDestroy$ = new Subject<void>()

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly toolbarService: ToolbarService,
    private readonly settingsService: SettingsService,
    private readonly notificationService: NotificationService,
    private readonly platformSettingsService: PlatformSettingsService,
    private readonly permissionsService: PermissionsService,
    private readonly featureFlagService: FeatureFlagService,
    public readonly sosService: SosService,
  ) {}

  ngOnInit(): void {
    const currentUrl = this.router.url
    this.isMapPage = currentUrl.includes('/client/map')

    // Acknowledging an SOS clears the matching bell notification too (John): decrement the badge and
    // post the read. Wired before the notifications-permission early-return so it holds on every page.
    this.sosService.notificationRead$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((notificationId) => this.markNotificationRead(notificationId))

    // v2: keep the historical "don't init notifications on the map page" behavior byte-for-byte.
    // v3: alerts are app-wide (Q2) — let the list mount on the map too and rely on frame-level
    // popup/sound suppression (handleRouteChange + notification-list's !isMapPage() sound gate).
    if (
      (this.isMapPage && !this.featureFlagService.realtimeV3) ||
      !this.hasPermission(PermissionsSource['CONFIGURATION.NOTIFICATIONS_LIST'])
    ) {
      return
    }

    this.isNotificationsPermission = true

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.onDestroy$),
      )
      .subscribe((event: NavigationEnd) => {
        this.isMapPage = event.urlAfterRedirects.includes('/client/map')
        this.handleRouteChange(event.urlAfterRedirects)
      })

    this.platformSettingsService.platformSettings$
      .pipe(filter(Boolean), takeUntil(this.onDestroy$))
      .subscribe((settings: PlatformSettings) => {
        const path = settings?.linkKnowledgeBase ? 'knowledge-base' : 'support'
        this.supportUrl = `/${this.user.teamType}/${path}`
      })
  }
  private handleRouteChange(url: string): void {
    if (url.includes('/client/map')) {
      this.isNotificationPopup = false
      this.isNotificationSound = false
      return
    }

    this.getData()
  }

  private getData(): void {
    const popup$: Observable<Setting> = this.settingsService.getUserNotificationPopupSetting(this.user.id)
    const sound$: Observable<Setting> = this.settingsService.getUserNotificationSoundSetting(this.user.id)
    const count$: Observable<number> = this.notificationService.getUnreadCount()

    forkJoin([popup$, sound$, count$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([popupSetting, soundSetting, count]) => {
        this.isNotificationPopup = !!popupSetting?.value
        this.isNotificationSound = !!soundSetting?.value
        this.unreadNotificationsCount = count

        this.settingsService['isNotificationPopup'].next(this.isNotificationPopup)
        this.settingsService['isNotificationSound'].next(this.isNotificationSound)
      })
  }

  loadNotificationList(): void {
    this.notificationList?.loadNotifications()
  }

  markNotificationRead(id: string): void {
    this.unreadNotificationsCount = Math.max(0, this.unreadNotificationsCount - 1)
    this.notificationService.markAsRead(id).subscribe()
  }

  markAllNotificationsRead(): void {
    if (this.unreadNotificationsCount > 0) {
      this.unreadNotificationsCount = 0
      this.notificationService.markAllAsRead().subscribe()
    }
  }

  onNotificationsArrived(notifications: NotificationMessage[]): void {
    this.unreadNotificationsCount += notifications.length
  }

  logout(): void {
    this.authService.logout().subscribe((response) => {
      this.authService.purgeAuth()
      // Only follow a safe http(s)/relative redirect so a reflected value cannot cause an open redirect.
      const safeRedirect = safeHttpUrl(response.redirectUrl)
      safeRedirect ? window.open(safeRedirect, '_self') : this.router.navigate(['login'])
    })
  }

  get getSupportUrl(): string {
    return this.supportUrl
  }

  get notificationUrl(): string {
    return this.user.isAdmin() ? '/admin/config/notifications-details' : '/client/configuration/notifications-details'
  }

  get configurationUrl(): string {
    return this.user.isAdmin() ? '/admin/config' : '/client/configuration'
  }

  get isNotifications(): boolean {
    return this.hasPermission(PermissionsSource['CONFIGURATION.NOTIFICATIONS_LIST'])
  }

  get isSupport(): boolean {
    return this.hasPermission(PermissionsSource['SUPPORT'])
  }

  get isMessenger(): boolean {
    return this.hasPermission(PermissionsSource.MESSENGER)
  }

  get isConfigurations(): boolean {
    return this.hasPermission(PermissionsSource['CONFIGURATION'])
  }

  // Entry point to the v2 client UI — only for client users and only when v2 routes are registered
  // (FeatureFlagService.uiV2 gates /v2/client via canMatch, so this stays hidden wherever it is off).
  get canSwitchToUiV2(): boolean {
    return this.featureFlagService.uiV2 && this.user?.isClient() === true
  }

  private hasPermission(permission: PermissionSourceType): boolean {
    return this.permissionsService.hasPermissionSync(permission)
  }

  ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
